/**
 * Company Admin Pages
 */

import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { Link } from 'react-router-dom';
import { DebugAuth } from '../../components/DebugAuth';

// Export the full users management page
export { CompanyAdminUsersManagement } from './UsersManagement';

// Dashboard
export function CompanyAdminDashboard() {
  const { companyId } = useAuth();
  const { getCompanyStats } = useCompany();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [companyId]);

  const loadStats = async () => {
    const data = await getCompanyStats(companyId);
    setStats(data);
    setLoading(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <p className="meta">Cargando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="grid" style={{ gap: '24px' }}>
      <DebugAuth />
      <div>
        <h1 className="title" style={{ fontSize: '28px', marginBottom: '8px' }}>Dashboard</h1>
        <p className="meta">Panel de control de tu empresa</p>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '16px' 
      }}>
        <StatCard icon="👥" label="Usuarios" value={stats?.totalUsers || 0} />
        <StatCard icon="📄" label="Documentos" value={stats?.totalDocuments || 0} />
        <StatCard icon="✅" label="Firmados" value={stats?.signedDocuments || 0} />
        <StatCard icon="⏳" label="Pendientes" value={stats?.pendingDocuments || 0} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
        <QuickActionCard
          icon="👥"
          title="Gestionar Usuarios"
          description="Ver y administrar usuarios de tu empresa"
          to="/admin/users"
        />
        <QuickActionCard
          icon="📄"
          title="Ver Documentos"
          description="Consultar todos los documentos"
          to="/admin/documents"
        />
      </div>
    </div>
  );
}

// Users
export function CompanyAdminUsers() {
  const { companyId } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, [companyId]);

  const loadUsers = async () => {
    const q = query(collection(db, 'users'), where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    setLoading(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <p className="meta">Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="grid" style={{ gap: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 className="title" style={{ fontSize: '28px', marginBottom: '8px' }}>Usuarios</h1>
          <p className="meta">Gestiona los usuarios de tu empresa</p>
        </div>
      </div>

      <div className="grid" style={{ gap: '12px' }}>
        {users.length === 0 ? (
          <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
            <p className="meta">No hay usuarios</p>
          </div>
        ) : (
          users.map(user => (
            <div key={user.id} className="card" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>
                    {user.name || user.email}
                  </h3>
                  <p className="meta">
                    {user.email} • {user.role === 'company_admin' ? '🏢 Admin' : '👤 Usuario'}
                  </p>
                </div>
                <span className="badge" style={{ 
                  background: user.active ? '#059669' : '#6b7280' 
                }}>
                  {user.active ? '✅ Activo' : '⏸️ Inactivo'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Documents
export function CompanyAdminDocuments() {
  const { companyId } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDocuments();
  }, [companyId]);

  const loadDocuments = async () => {
    // Buscar documentos de la empresa actual (CEJM)
    const q = query(collection(db, 'requests'), where('companyId', '==', companyId));
    const snapshot = await getDocs(q);
    const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('Documentos cargados para companyId:', companyId, 'Total:', docs.length);
    setDocuments(docs);
    setLoading(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <p className="meta">Cargando documentos...</p>
      </div>
    );
  }

  return (
    <div className="grid" style={{ gap: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 className="title" style={{ fontSize: '28px', marginBottom: '8px' }}>Documentos</h1>
          <p className="meta">Todos los documentos de tu empresa</p>
        </div>
        <Link to="/upload" className="btn">
          ➕ Subir Documento
        </Link>
      </div>

      <div className="grid" style={{ gap: '12px' }}>
        {documents.length === 0 ? (
          <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
            <p className="meta">No hay documentos todavía</p>
            <Link to="/upload" className="btn" style={{ marginTop: '16px', display: 'inline-block' }}>
              Subir primer documento
            </Link>
          </div>
        ) : (
          documents.map(doc => (
            <div key={doc.id} className="card" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>
                    {doc.originalName || 'Sin nombre'}
                  </h3>
                  <p className="meta">
                    Creado: {doc.createdAt?.toDate?.()?.toLocaleDateString() || 'Fecha desconocida'}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span className="badge" style={{ 
                    background: doc.status === 'signed' ? '#059669' : '#ca8a04' 
                  }}>
                    {doc.status === 'signed' ? '✅ Firmado' : '⏳ Pendiente'}
                  </span>
                  {doc.status === 'pending' && (
                    <Link to={`/sign/${doc.id}`} className="copy">
                      Firmar
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Settings
export function CompanyAdminSettings() {
  const { selectedCompany } = useCompany();

  return (
    <div className="grid" style={{ gap: '24px' }}>
      <div>
        <h1 className="title" style={{ fontSize: '28px', marginBottom: '8px' }}>Configuración</h1>
        <p className="meta">Ajustes de tu empresa</p>
      </div>

      <div className="card">
        <h2 className="title" style={{ fontSize: '20px', marginBottom: '16px' }}>
          Información de la Empresa
        </h2>
        <div className="grid" style={{ gap: '16px' }}>
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: '8px' }}>
              Nombre
            </label>
            <input
              type="text"
              defaultValue={selectedCompany?.name}
              disabled
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: '8px' }}>
              Tipo
            </label>
            <input
              type="text"
              defaultValue={selectedCompany?.type}
              disabled
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="title" style={{ fontSize: '20px', marginBottom: '16px' }}>
          Branding
        </h2>
        <div className="grid" style={{ gap: '16px' }}>
          <div>
            <label className="kicker" style={{ display: 'block', marginBottom: '8px' }}>
              Color Primario
            </label>
            <input
              type="color"
              defaultValue={selectedCompany?.branding?.primaryColor || '#4f6cf7'}
              style={{ height: '40px', width: '80px' }}
            />
          </div>
        </div>
        <div style={{ marginTop: '24px' }}>
          <button className="btn">
            Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="card" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ 
          fontSize: '32px',
          background: 'var(--primary)',
          borderRadius: '12px',
          padding: '12px',
          lineHeight: 1
        }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <p className="kicker" style={{ marginBottom: '4px' }}>{label}</p>
          <p style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>{value}</p>
        </div>
      </div>
    </div>
  );
}

function QuickActionCard({ icon, title, description, to }) {
  return (
    <Link
      to={to}
      className="card"
      style={{ 
        display: 'block',
        padding: '20px',
        textDecoration: 'none',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 108, 247, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        <span style={{ fontSize: '36px', lineHeight: 1 }}>{icon}</span>
        <div>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>{title}</h3>
          <p className="meta">{description}</p>
        </div>
      </div>
    </Link>
  );
}
